import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ModalRef } from "@euka-mfes/bark";

import { useStudentData } from "@/context";
import type { Grade } from "@/ts/interfaces/student/studentAccountInterface";
import SelectGradeModal from "@components/SelectGradeModal";
import PinInputModal from "@components/student/PinInputModal";
import { GRADE_STATUS } from "@ts/enums/GradeStatus";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { logger } from "@utils/loggingUtils";
import { getBaseRoute } from "@utils/route";
import {
  ensureGradeInPath,
  mapGradeCodeToGradeParamId,
  replaceGradeInPath
} from "@utils/routeUtil";

/** Grade switcher and parent-PIN activation flow. Registers its opener so the context can show it. */
const GradeActivationModals = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const base = getBaseRoute();

  const {
    headerDetails,
    changedGrade,
    setChangedGrade,
    setActivatingGradeCode,
    fetchGradeChangeOtp,
    validateGradeChangeOtp,
    fetchHeaderDetails,
    getGradeChangeOtpRequestStatus,
    validateGradeChangeOtpRequestStatus,
    resetTermsDetails,
    refetchStructure,
    registerGradePickerOpener
  } = useStudentData();

  const gradeSelectModalRef = useRef<ModalRef | null>(null);
  const otpModalRef = useRef<ModalRef | null>(null);
  const closingForOtpRef = useRef(false);
  const closingForValidateOtpRef = useRef(false);

  const [pinCodes, setPinCodes] = useState<string[]>([]);
  const [validateOTPError, setValidateOTPError] = useState("");
  const [getOTPError, setGetOTPError] = useState("");
  const [targetGrade, setTargetGrade] = useState<Grade | null>(null);

  useEffect(() => {
    registerGradePickerOpener(() => gradeSelectModalRef.current?.show());
    return () => registerGradePickerOpener(null);
  }, [registerGradePickerOpener]);

  const updateUrlGradeParam = (newGrade: Grade) => {
    const newGradeParam = mapGradeCodeToGradeParamId(
      newGrade.grade_code
    ) as string;

    if (pathname.includes("/orientation")) {
      return;
    }

    // Assignments paths are grade-specific — land on the new grade's dashboard.
    if (pathname.includes("/assignments")) {
      navigate(`${base}${newGradeParam}`, { replace: true });
      return;
    }

    const next = pathname.startsWith(base)
      ? replaceGradeInPath(pathname, newGradeParam)
      : ensureGradeInPath(pathname, newGradeParam);

    if (next !== pathname) navigate(next, { replace: true });
  };

  const onPinVerification = async () => {
    setValidateOTPError("");

    const pin = pinCodes.join("");

    try {
      await validateGradeChangeOtp(pin);

      closingForValidateOtpRef.current = true;
      otpModalRef.current?.dismiss();

      resetTermsDetails();

      if (changedGrade) {
        await fetchHeaderDetails();
        updateUrlGradeParam(changedGrade);
      }

      // After the URL grade is settled — fetch fresh structure so the newly-
      // activated term's has_active_entitlement flag is current.
      refetchStructure();
      setPinCodes(["", "", "", ""]);
      setChangedGrade(null);
      setActivatingGradeCode(null);
    } catch (error: unknown) {
      const err = error as {
        code?: number;
        customAttributes?: { httpErrorStatus?: number };
        message?: string;
      };
      if (
        err?.customAttributes?.httpErrorStatus === 403 ||
        err?.code === 403
      ) {
        setValidateOTPError(
          "Sorry, that code is incorrect, or has expired. Please try again, or request a new code."
        );
      } else {
        setValidateOTPError(err?.message ?? "Validation failed");
      }

      logger.error("VALIDATE OTP API ERROR", error);
    }
  };

  const handleRequestNewCode = async () => {
    setValidateOTPError("");
    setPinCodes(["", "", "", ""]);

    try {
      await fetchGradeChangeOtp(targetGrade?.grade_code as number);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setValidateOTPError(err?.message ?? "Failed to get new code");
      logger.error("GET OTP API ERROR", error);
    }
  };

  const handlePinCancel = () => {
    setValidateOTPError("");
    setPinCodes(["", "", "", ""]);
    setTargetGrade(null);
    setActivatingGradeCode(null);
    setChangedGrade(null);
  };

  const getOtp = async (grade: Grade) => {
    setGetOTPError("");
    setTargetGrade(grade);
    setActivatingGradeCode(grade.grade_code);

    try {
      await fetchGradeChangeOtp(grade?.grade_code);
      closingForOtpRef.current = true;
      gradeSelectModalRef.current?.dismiss();
      otpModalRef.current?.show();
    } catch (error: unknown) {
      const err = error as { message?: string };
      setGetOTPError(err?.message ?? "Failed to get OTP");
      logger.error("GET OTP API ERROR", error);
    }
  };

  const handleGradeSelect = (selectedNewGrade: Grade) => {
    setChangedGrade(selectedNewGrade);

    if (
      selectedNewGrade.status === GRADE_STATUS.CURRENT ||
      selectedNewGrade.status === GRADE_STATUS.ACTIVE
    ) {
      gradeSelectModalRef.current?.dismiss();
      setTargetGrade(null);
      setChangedGrade(null);
      updateUrlGradeParam(selectedNewGrade);
      return;
    }

    if (selectedNewGrade.status === GRADE_STATUS.NOT_STARTED)
      getOtp(selectedNewGrade);
  };

  const handleGradeChangeCancel = () => {
    setGetOTPError("");
    setChangedGrade(null);
  };

  return (
    <>
      <SelectGradeModal
        modalRef={gradeSelectModalRef}
        grades={headerDetails.grades}
        handleGradeSelect={handleGradeSelect}
        errorMessage={getOTPError}
        handleGradeChangeCancel={handleGradeChangeCancel}
        isOtpLoading={getGradeChangeOtpRequestStatus === REQUEST_STATUS.PENDING}
        isProgrammaticCloseRef={closingForOtpRef}
      />

      <PinInputModal
        modalRef={otpModalRef}
        pinCodes={pinCodes}
        onPinVerification={onPinVerification}
        handleRequestNewCode={handleRequestNewCode}
        isRequestNewCode={
          getGradeChangeOtpRequestStatus === REQUEST_STATUS.PENDING
        }
        handleInputChange={setPinCodes}
        isSubmitOTP={
          validateGradeChangeOtpRequestStatus === REQUEST_STATUS.PENDING
        }
        errorMessage={validateOTPError}
        handlePinCancel={handlePinCancel}
        title={`Start ${targetGrade?.name}`}
        descriptionLineOne="We need to make sure parents know when new grades commence because they may be required to provide enrolment details."
        descriptionLineTwo={`To start a ${targetGrade?.name}, enter the pin code we sent to your parent’s email address.`}
        isProgrammaticCloseRef={closingForValidateOtpRef}
      />
    </>
  );
};

export default GradeActivationModals;
